import React from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'

import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import UserIcon from 'src/components/ui/UserIcon'
import { useAccountSelect } from 'src/hooks/useAccount'
import { useAppAction } from 'src/hooks/useApp'
import { usePresenceAction } from 'src/hooks/usePresence'
import { presenceService } from 'src/services/presence'
import { TManualUserStatus, TUserStatus } from 'src/services/auth/users'
import { getStatusColor } from './statusColor'

interface IOption {
  value: TManualUserStatus
  label: string
  hint: string
  /** colour of the dot */
  status: TUserStatus
}

/**
 * Section of the profile menu: Automatic / Do not disturb / Appear offline.
 * "Automatic" is what the API calls `online`: the status follows presence again.
 */
const StatusPicker = () => {
  const { t } = useTranslation('presence')
  const theme = useTheme()
  const presence = useAccountSelect.user()?.presence
  const setStatus = usePresenceAction.setStatus()
  const toast = useAppAction.tost()

  const [canImproveDetection, setCanImproveDetection] = React.useState(
    presenceService.activity.isSystemIdleDetectionAvailable() &&
      !presenceService.activity.isSystemIdleDetectionActive()
  )

  // an old backend: nothing to pick yet
  if (!presence) return null

  const selected: TManualUserStatus = presence.statusIsManual
    ? (presence.status as TManualUserStatus)
    : 'online'

  const options: IOption[] = [
    {
      value: 'online',
      label: presence.statusIsManual
        ? t('picker.automatic')
        : `${t('picker.automatic')} · ${t(`status.${presence.status}`)}`,
      hint: t('picker.automaticHint'),
      status: presence.statusIsManual ? 'online' : presence.status,
    },
    {
      value: 'dnd',
      label: t('picker.dnd'),
      hint: t('picker.dndHint'),
      status: 'dnd',
    },
    {
      value: 'offline',
      label: t('picker.offline'),
      hint: t('picker.offlineHint'),
      status: 'offline',
    },
  ]

  // must run inside the click: browsers only show the permission prompt on a user gesture
  const improveDetectionHandler = async () => {
    const enabled = await presenceService.activity.enableSystemIdleDetection()

    setCanImproveDetection(!enabled)
    toast({
      type: enabled ? 'success' : 'warning',
      description: t(
        enabled ? 'picker.awayDetectionEnabled' : 'picker.awayDetectionDenied'
      ),
    })
  }

  return (
    <>
      <Typography px={2.5} pb={1} fontSize={12} color="text.secondary">
        {t('picker.title')}
      </Typography>

      {options.map((option) => (
        <Tooltip key={option.value} title={option.hint} placement="left">
          <MenuItem
            selected={option.value === selected}
            onClick={() => {
              if (option.value !== selected) setStatus({ status: option.value })
            }}
          >
            <Stack
              spacing={2}
              direction="row"
              alignItems="center"
              sx={{ width: '100%' }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  flexShrink: 0,
                  backgroundColor: getStatusColor(theme, option.status),
                }}
              />
              <Typography color="inherit" fontSize={14} noWrap>
                {option.label}
              </Typography>
            </Stack>
          </MenuItem>
        </Tooltip>
      ))}

      {canImproveDetection && (
        <Tooltip title={t('picker.improveAwayDetectionHint')} placement="left">
          <MenuItem onClick={improveDetectionHandler}>
            <Stack spacing={1} direction="row" alignItems="center">
              <UserIcon icon="tabler:radar-2" fontSize={18} />
              <Typography color="inherit" fontSize={13} noWrap>
                {t('picker.improveAwayDetection')}
              </Typography>
            </Stack>
          </MenuItem>
        </Tooltip>
      )}
    </>
  )
}

export default StatusPicker
